import React from 'react';
import { getAgingReport } from '../../lib/accountsPayableReportsApi';
import AgingTable from './AgingTable';
import { formatReportDay } from './format';
import { IndicativeLine, ReportFrame } from './ReportFrame';
import { useReport } from './useReport';

/**
 * One supplier's aging, on the supplier's own page (D49 phase 2). It is the server's aging report
 * filtered to this supplier: one table per currency, so a TTD bill and a USD bill are never added
 * together, and every figure links to the bills behind it.
 *
 * It replaced a browser-side version that added TTD and USD into one "30 days" figure, aged by
 * the browser's clock, and only looked at the first 200 bills.
 */
export const SupplierAging: React.FC<{ supplierName: string }> = ({ supplierName }) => {
  const state = useReport(async () => {
    const res = await getAgingReport({ supplier: supplierName });
    return res.data;
  }, [supplierName]);

  return (
    <section aria-label="Aging" className="mb-4">
      <h2 className="mb-1 text-sm font-semibold text-slate-900">Aging</h2>
      <p className="mb-3 text-xs text-slate-500">
        What is still owed to this supplier, by how many days past its due date. Each currency has its own
        table.
      </p>
      <ReportFrame
        state={state}
        isEmpty={(data) => data.currencies.length === 0}
        emptyTitle="Nothing is owed to this supplier."
        emptyDetail="Every bill is paid or credited in full."
      >
        {(data) => (
          <>
            <p className="mb-3 text-xs text-slate-500">Aged as of {formatReportDay(data.asOf)}.</p>
            {data.currencies.map((block) => (
              <AgingTable key={block.currency} report={data} block={block} onlySupplier={supplierName} />
            ))}
            <IndicativeLine
              indicative={data.indicative}
              figureKey="total"
              currencyCount={data.currencies.length}
              what="total owed"
            />
          </>
        )}
      </ReportFrame>
    </section>
  );
};

export default SupplierAging;
